/**
 * AI Action Panel Component - Minimalist Design
 * Clean, focused action items with subtle elegance
 */
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  FileText, 
  Clock, 
  CheckCircle, 
  Calendar,
  Sparkles,
  X,
  RefreshCw,
  Send,
  ChevronRight,
  ArrowRight,
  Wand2,
  ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../../../utils/api';
import { useRecommendedActions, ActionItem } from '../hooks/useRecommendedActions';

interface ActionPanelProps {
  onNavigate?: (url: string) => void;
  language?: string;
}

// Minimal priority indicators
const priorityDot = {
  critical: 'bg-red-500',
  high: 'bg-orange-400',
  medium: 'bg-yellow-400',
  low: 'bg-gray-300'
};

// Action type icons - subtle
const typeIcons: Record<string, React.ReactNode> = {
  reply_email: <Mail className="w-4 h-4" />,
  review_document: <FileText className="w-4 h-4" />,
  follow_up: <Clock className="w-4 h-4" />,
  complete_task: <CheckCircle className="w-4 h-4" />,
  prepare_meeting: <Calendar className="w-4 h-4" />
};

// AI Improvement types with icons
const AI_IMPROVEMENTS = {
  professional: { en: 'More Professional', no: 'Mer profesjonell', icon: '✨' },
  concise: { en: 'More Concise', no: 'Mer kortfattet', icon: '📝' },
  friendly: { en: 'Warmer Tone', no: 'Varmere tone', icon: '💬' },
  formal: { en: 'More Formal', no: 'Mer formell', icon: '📋' },
  general: { en: 'Improve Overall', no: 'Forbedre generelt', icon: '🚀' }
};

// World-Class Email Composer Modal - Ultra Minimalist Design
const EmailEditorModal: React.FC<{
  action: ActionItem;
  onClose: () => void;
  onSend: (data: { to: string; cc: string; bcc: string; subject: string; body: string }) => void;
  language: string;
}> = ({ action, onClose, onSend, language }) => {
  const [to, setTo] = useState(action.email_to || '');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState(action.email_subject || '');
  const [body, setBody] = useState(action.ai_draft || '');
  const [isSending, setIsSending] = useState(false);
  const [isImproving, setIsImproving] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [showAiMenu, setShowAiMenu] = useState(false);
  const editorRef = React.useRef<HTMLDivElement>(null);
  
  const isEnglish = language === 'en';
  const t = (en: string, no: string) => isEnglish ? en : no;

  // Format AI draft with proper structure - World-Class email formatting
  const formatEmailContent = (text: string) => {
    if (!text) return '';
    
    let formatted = text;
    
    // Convert markdown-style bold to HTML
    formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    
    // Convert markdown-style italic to HTML
    formatted = formatted.replace(/\*(.+?)\*/g, '<em>$1</em>');
    
    // Split and process lines
    const lines = formatted.split('\n');
    let inList = false;
    let listType = 'ul';
    const processedLines: string[] = [];
    let skipNextEmpty = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      const bulletMatch = line.match(/^[-•*]\s+(.+)$/);
      const numberedMatch = line.match(/^(\d+)[.)]\s+(.+)$/);
      
      // Skip consecutive empty lines
      if (line === '') {
        if (skipNextEmpty) continue;
        skipNextEmpty = true;
      } else {
        skipNextEmpty = false;
      }
      
      if (bulletMatch) {
        if (!inList) {
          processedLines.push('<ul style="margin: 8px 0 12px 0; padding-left: 24px; list-style-type: disc;">');
          inList = true;
          listType = 'ul';
        }
        processedLines.push(`<li style="margin: 4px 0; color: #1f2937; line-height: 1.5;">${bulletMatch[1]}</li>`);
      } else if (numberedMatch) {
        if (!inList || listType !== 'ol') {
          if (inList) processedLines.push(`</${listType}>`);
          processedLines.push('<ol style="margin: 8px 0 12px 0; padding-left: 24px; list-style-type: decimal;">');
          inList = true;
          listType = 'ol';
        }
        processedLines.push(`<li style="margin: 4px 0; color: #1f2937; line-height: 1.5;">${numberedMatch[2]}</li>`);
      } else {
        if (inList) {
          processedLines.push(`</${listType}>`);
          inList = false;
        }
        
        if (line === '') {
          // Minimal spacing between paragraphs
          processedLines.push('<div style="height: 8px;"></div>');
        } else if (line.toLowerCase().startsWith('hei') || 
                   line.toLowerCase().startsWith('kjære') ||
                   line.toLowerCase().startsWith('dear')) {
          // Greeting
          processedLines.push(`<p style="margin: 0 0 12px 0; color: #111827;">${line}</p>`);
        } else if (line.toLowerCase().includes('vennlig hilsen') || 
                   line.toLowerCase().includes('beste hilsen') ||
                   line.toLowerCase().includes('mvh') ||
                   line.toLowerCase().includes('best regards') ||
                   line.toLowerCase().includes('kind regards')) {
          // Closing salutation
          processedLines.push(`<p style="margin: 16px 0 2px 0; color: #374151;">${line}</p>`);
        } else if (line.match(/^[A-ZÆØÅ][a-zæøå]+\s[A-ZÆØÅ]/) && lines[i-1]?.toLowerCase().includes('hilsen')) {
          // Name after salutation
          processedLines.push(`<p style="margin: 0; font-weight: 500; color: #111827;">${line}</p>`);
        } else if (line.match(/^[A-Z\*].*:$/)) {
          // Section header (ends with colon, starts with capital or bold)
          processedLines.push(`<p style="margin: 14px 0 6px 0; font-weight: 600; color: #111827;">${line}</p>`);
        } else {
          // Regular paragraph - tight spacing
          processedLines.push(`<p style="margin: 0 0 8px 0; line-height: 1.6; color: #374151;">${line}</p>`);
        }
      }
    }
    
    if (inList) {
      processedLines.push(`</${listType}>`);
    }
    
    return processedLines.join('');
  };

  const handleSend = async () => {
    setIsSending(true);
    try {
      const content = editorRef.current?.innerHTML || body;
      await onSend({ to, cc, bcc, subject, body: content });
      onClose();
    } catch (err) {
      console.error('Failed to send:', err);
    } finally {
      setIsSending(false);
    }
  };

  const handleImproveText = async (improvementType: string) => {
    setIsImproving(true);
    setShowAiMenu(false);
    
    try {
      const token = localStorage.getItem('token');
      const currentText = editorRef.current?.innerText || body;
      
      const response = await axios.post(
        `${API_URL}/ai/improve-text`,
        { text: currentText, improvement_type: improvementType, lang: language },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.improved_text) {
        if (editorRef.current) {
          editorRef.current.innerHTML = formatEmailContent(response.data.improved_text);
        }
        setBody(response.data.improved_text);
      }
    } catch (err) {
      console.error('Failed to improve text:', err);
    } finally {
      setIsImproving(false);
    }
  };

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.98, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.98, opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden"
        style={{ maxHeight: '90vh', minWidth: '700px' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Clean Header */}
        <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center text-white text-sm font-medium">
              {action.email_from?.charAt(0).toUpperCase() || 'J'}
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                {action.email_from}
              </h2>
              <p className="text-sm text-gray-500 truncate max-w-sm">
                {action.email_subject}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Subtle AI indicator */}
        <div className="px-6 py-2 bg-gray-50/50 border-b border-gray-100 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-xs text-gray-500">
            {t('AI-generated draft', 'AI-generert utkast')} · {t('Ready for review', 'Klar for gjennomgang')}
          </span>
        </div>

        {/* Email Fields */}
        <div className="px-6 py-3 space-y-2 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-400 w-14">{t('To', 'Til')}</label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="flex-1 text-sm text-gray-900 bg-transparent focus:outline-none"
              placeholder={t('recipient@email.com', 'mottaker@epost.no')}
            />
            {!showCcBcc && (
              <button 
                onClick={() => setShowCcBcc(true)}
                className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
              >
                Cc/Bcc
              </button>
            )}
          </div>

          <AnimatePresence>
            {showCcBcc && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="space-y-2 overflow-hidden"
              >
                <div className="flex items-center gap-3">
                  <label className="text-sm text-gray-400 w-14">Cc</label>
                  <input
                    type="email"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    className="flex-1 text-sm text-gray-900 bg-transparent focus:outline-none"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm text-gray-400 w-14">Bcc</label>
                  <input
                    type="email"
                    value={bcc}
                    onChange={(e) => setBcc(e.target.value)}
                    className="flex-1 text-sm text-gray-900 bg-transparent focus:outline-none"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-400 w-14">{t('Subject', 'Emne')}</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="flex-1 text-sm font-medium text-gray-900 bg-transparent focus:outline-none"
            />
          </div>
        </div>

        {/* Minimal Toolbar */}
        <div className="px-4 py-2 flex items-center gap-1 border-b border-gray-100">
          <button onClick={() => execCommand('bold')} className="p-2 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700">
            <span className="font-bold text-xs">B</span>
          </button>
          <button onClick={() => execCommand('italic')} className="p-2 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700">
            <span className="italic text-xs">I</span>
          </button>
          <button onClick={() => execCommand('underline')} className="p-2 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700">
            <span className="underline text-xs">U</span>
          </button>
          
          <div className="w-px h-4 bg-gray-200 mx-1" />
          
          <button onClick={() => execCommand('insertUnorderedList')} className="p-2 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="flex-1" />
          
          {/* AI Improve Button - Minimal */}
          <div className="relative">
            <button 
              onClick={() => setShowAiMenu(!showAiMenu)}
              disabled={isImproving}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                isImproving 
                  ? 'bg-gray-100 text-gray-400' 
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              {isImproving ? (
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Wand2 className="w-3.5 h-3.5" />
              )}
              <span>{isImproving ? t('...', '...') : t('Improve', 'Forbedre')}</span>
            </button>
            
            <AnimatePresence>
              {showAiMenu && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[180px] z-50"
                >
                  {Object.entries(AI_IMPROVEMENTS).map(([key, item]) => (
                    <button
                      key={key}
                      onClick={() => handleImproveText(key)}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <span>{item.icon}</span>
                      <span>{isEnglish ? item.en : item.no}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Email Body - World-Class Editor */}
        <div className="flex-1 overflow-y-auto min-h-0 bg-white">
          <div
            ref={editorRef}
            contentEditable
            className="w-full min-h-full px-6 py-5 text-[15px] text-gray-800 focus:outline-none"
            style={{ 
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              lineHeight: '1.6'
            }}
            dangerouslySetInnerHTML={{ __html: formatEmailContent(body) }}
            onKeyDown={(e) => {
              if (e.ctrlKey || e.metaKey) {
                if (e.key === 'b') { e.preventDefault(); execCommand('bold'); }
                if (e.key === 'i') { e.preventDefault(); execCommand('italic'); }
                if (e.key === 'u') { e.preventDefault(); execCommand('underline'); }
              }
            }}
          />
        </div>

        {/* Original Message - Collapsible */}
        {action.email_body_preview && (
          <details className="border-t border-gray-100">
            <summary className="px-6 py-3 text-sm text-gray-400 cursor-pointer hover:text-gray-600 flex items-center gap-2">
              <ChevronRight className="w-4 h-4 transition-transform [details[open]>&]:rotate-90" />
              {t('Original message', 'Original melding')}
            </summary>
            <div className="px-6 pb-4">
              <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-500 leading-relaxed">
                <p className="text-xs text-gray-400 mb-2">
                  {t('From', 'Fra')}: {action.email_from}
                </p>
                <p>{action.email_body_preview}</p>
              </div>
            </div>
          </details>
        )}

        {/* Clean Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          {/* Context Tags - Minimal */}
          <div className="flex items-center gap-2">
            {action.case_title && (
              <span className="text-xs text-gray-400">
                {action.case_title}
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t('Discard', 'Forkast')}
            </button>
            <button
              onClick={handleSend}
              disabled={isSending || !to.trim()}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 hover:bg-gray-800 transition-colors"
            >
              {isSending ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {t('Send', 'Send')}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Single Action Item - Apple-style minimal
const ActionItemCard: React.FC<{
  action: ActionItem;
  onAction: (action: ActionItem) => void;
  onDismiss: (id: string) => void;
  language: string;
  index: number;
}> = ({ action, onAction, onDismiss, language, index }) => {
  const isEnglish = language === 'en';
  const t = (en: string, no: string) => isEnglish ? en : no;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('🎯 ActionItemCard clicked!', action.title, action.type);
    onAction(action);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      onClick={handleClick}
      className="w-full text-left group cursor-pointer"
      role="button"
      tabIndex={0}
    >
      <div className="flex items-start gap-4 py-4 border-b border-gray-100 group-hover:border-gray-200 transition-colors">
        {/* Priority indicator - subtle dot */}
        <div className={`w-2 h-2 rounded-full mt-2 ${priorityDot[action.priority]}`} />
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[15px] text-gray-900 font-medium">
              {action.title}
            </p>
            {action.ai_ready && (
              <span className="text-xs text-violet-500">
                {t('AI ready', 'AI klar')}
              </span>
            )}
          </div>
          
          {action.email_subject && (
            <p className="text-sm text-gray-500 mt-0.5 truncate">
              {action.email_subject}
            </p>
          )}
          
          {action.case_title && (
            <p className="text-xs text-gray-400 mt-1">
              {action.case_title}
              {action.client_name && ` · ${action.client_name}`}
            </p>
          )}
        </div>
        
        {/* Time + Arrow */}
        <div className="flex items-center gap-3 text-gray-400 group-hover:text-gray-600 transition-colors">
          <span className="text-xs">{action.estimated_time}</span>
          <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </motion.div>
  );
};

// Main Action Panel - Apple/OpenAI Minimal Style
export const ActionPanel: React.FC<ActionPanelProps> = ({
  onNavigate,
  language = 'no'
}) => {
  const { 
    actions, 
    isLoading, 
    error,
    summary,
    refetch, 
    executeAction,
    dismissAction 
  } = useRecommendedActions(language);

  const [selectedAction, setSelectedAction] = useState<ActionItem | null>(null);
  const isEnglish = language === 'en';
  const t = (en: string, no: string) => isEnglish ? en : no;

  const handleAction = (action: ActionItem) => {
    console.log('🔵 ActionPanel handleAction called');
    console.log('🔵 Action type:', action.type);
    console.log('🔵 Action id:', action.id);
    console.log('🔵 Full action:', JSON.stringify(action, null, 2));
    
    if (action.type === 'reply_email' || action.type === 'follow_up') {
      console.log('🟢 Setting selectedAction for email modal');
      setSelectedAction(action);
      console.log('🟢 selectedAction should now be set');
    } else if (action.action_url && onNavigate) {
      console.log('🟡 Navigating to:', action.action_url);
      onNavigate(action.action_url);
    } else {
      console.log('🔴 No action taken - type:', action.type, 'action_url:', action.action_url);
    }
  };

  const handleSendEmail = async (data: { to: string; cc: string; bcc: string; subject: string; body: string }) => {
    if (!selectedAction) return;
    dismissAction(selectedAction.id);
    setSelectedAction(null);
  };

  // Loading state - minimal
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-16 bg-gray-50 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  // Empty state - minimal
  if (error || actions.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-gray-400 text-sm">
          {t('Nothing urgent. You\'re all caught up.', 'Ingenting haster. Alt er i orden.')}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Actions list - clean */}
      <div>
        <AnimatePresence mode="popLayout">
          {actions.slice(0, 4).map((action, index) => (
            <ActionItemCard
              key={action.id}
              action={action}
              onAction={handleAction}
              onDismiss={dismissAction}
              language={language}
              index={index}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Email Editor Modal - rendered via Portal to escape overflow-hidden */}
      {selectedAction && (selectedAction.type === 'reply_email' || selectedAction.type === 'follow_up') && 
        ReactDOM.createPortal(
          <EmailEditorModal
            action={selectedAction}
            onClose={() => setSelectedAction(null)}
            onSend={handleSendEmail}
            language={language}
          />,
          document.body
        )
      }
    </>
  );
};

export default ActionPanel;
